import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  getHiddenUserDetail,
  getHiddenUsers,
  reviewHiddenUserSignal,
} from '../../../services/reports';
import HiddenUserDetailContent from './HiddenUserDetailContent';

const { Text } = Typography;

const PAGE_SIZE_OPTIONS = ['10', '20', '50'];

export function readExplicitPendingCount(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (
    !Object.prototype.hasOwnProperty.call(payload, 'totalPendingSignalCount')
  ) {
    return null;
  }
  const n = Number(payload.totalPendingSignalCount);
  return Number.isFinite(n) ? n : null;
}

function formatUserFullName(user) {
  if (!user || typeof user !== 'object') return null;
  const fullName = [user.firstName, user.lastName, user.secondLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || null;
}

function formatPersonName(user) {
  if (!user || typeof user !== 'object') return '-';
  return formatUserFullName(user) || user.nickname || user.email || '-';
}

function mapHiddenUsersPayload(result) {
  if (Array.isArray(result)) {
    return { items: result, total: result.length, pendingCount: null };
  }
  const items = result?.items ?? result?.data ?? [];
  const list = Array.isArray(items) ? items : [];
  return {
    items: list,
    total: result?.total ?? list.length,
    pendingCount: readExplicitPendingCount(result),
  };
}

const HiddenUsersModal = ({
  open,
  onClose,
  onPreviewVideo,
  onGlobalPendingCountChange,
}) => {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const [reviewingSignalId, setReviewingSignalId] = useState(null);
  const onGlobalPendingCountChangeRef = useRef(onGlobalPendingCountChange);
  onGlobalPendingCountChangeRef.current = onGlobalPendingCountChange;

  const notifyPendingCount = useCallback((count) => {
    if (typeof onGlobalPendingCountChangeRef.current === 'function') {
      onGlobalPendingCountChangeRef.current(count);
    }
  }, []);

  const loadList = useCallback(
    async (page, pageSize) => {
      setLoading(true);
      try {
        const result = await getHiddenUsers({ page, pageSize });
        const mapped = mapHiddenUsersPayload(result);
        setItems(mapped.items);
        setTotal(mapped.total);
        notifyPendingCount(mapped.pendingCount);
      } catch {
        message.error('No fue posible consultar los usuarios bloqueados.');
      } finally {
        setLoading(false);
      }
    },
    [message, notifyPendingCount]
  );

  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;

  useEffect(() => {
    if (!open) {
      setSelectedDetail(null);
      setSelectedClientId(null);
      setLoadingDetailId(null);
      setReviewingSignalId(null);
      setPagination({ current: 1, pageSize: 10 });
      setItems([]);
      setTotal(0);
      return;
    }
    loadListRef.current(1, 10);
  }, [open]);

  const openDetail = useCallback(
    async (clientId) => {
      if (clientId == null || loadingDetailId != null) return;
      setLoadingDetailId(clientId);
      try {
        const result = await getHiddenUserDetail(clientId);
        setSelectedClientId(clientId);
        setSelectedDetail(result);
      } catch {
        message.error('No fue posible consultar el detalle del usuario bloqueado.');
      } finally {
        setLoadingDetailId(null);
      }
    },
    [loadingDetailId, message]
  );

  const backToList = useCallback(() => {
    if (reviewingSignalId != null) return;
    setSelectedDetail(null);
    setSelectedClientId(null);
  }, [reviewingSignalId]);

  const refreshAfterReview = useCallback(async () => {
    if (selectedClientId != null) {
      const result = await getHiddenUserDetail(selectedClientId);
      setSelectedDetail(result);
    }
    await loadList(pagination.current, pagination.pageSize);
  }, [loadList, pagination, selectedClientId]);

  const applyReview = useCallback(
    async (signalId) => {
      if (signalId == null || reviewingSignalId != null) return;
      setReviewingSignalId(signalId);
      try {
        await reviewHiddenUserSignal(signalId);
        await refreshAfterReview();
        message.success('Señal marcada como revisada.');
      } catch {
        message.error('No fue posible marcar la señal como revisada.');
      } finally {
        setReviewingSignalId(null);
      }
    },
    [message, refreshAfterReview, reviewingSignalId]
  );

  const confirmReview = useCallback(
    (signalId) => {
      if (signalId == null || reviewingSignalId != null) return;
      modal.confirm({
        title: '¿Marcar esta señal como revisada?',
        okText: 'Aceptar',
        cancelText: 'Cancelar',
        onOk: () => applyReview(signalId),
      });
    },
    [applyReview, modal, reviewingSignalId]
  );

  const handleTableChange = (pager) => {
    const nextPage = pager.current;
    const nextSize = pager.pageSize;
    setPagination({ current: nextPage, pageSize: nextSize });
    loadList(nextPage, nextSize);
  };

  const handleClose = () => {
    if (reviewingSignalId != null) return;
    onClose();
  };

  const columns = [
    {
      title: 'Usuario bloqueado',
      key: 'client',
      ellipsis: true,
      render: (_, row) => formatPersonName(row.client ?? row.hiddenClient),
    },
    {
      title: 'Bloqueado por',
      key: 'hiddenByCount',
      width: 140,
      render: (_, row) => {
        const count = Number(row.hiddenByCount) || 0;
        return count === 1 ? '1 usuario' : `${count} usuarios`;
      },
    },
    {
      title: 'Señales pendientes',
      key: 'pendingSignalCount',
      width: 160,
      render: (_, row) => {
        const count = Number(row.pendingSignalCount) || 0;
        if (count > 0) {
          return (
            <Tag color="warning">
              {count === 1 ? '1 pendiente' : `${count} pendientes`}
            </Tag>
          );
        }
        return <Text type="secondary">0</Text>;
      },
    },
    {
      title: 'Total de señales',
      key: 'totalSignalCount',
      width: 140,
      render: (_, row) => Number(row.totalSignalCount) || 0,
    },
    {
      title: 'Último bloqueo',
      dataIndex: 'lastHiddenAt',
      key: 'lastHiddenAt',
      width: 140,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Acción',
      key: 'action',
      width: 120,
      render: (_, row) => {
        const clientId = row.hiddenClientId ?? row.client?.id;
        return (
          <Button
            type="link"
            onClick={() => openDetail(clientId)}
            loading={loadingDetailId === clientId}
            disabled={loadingDetailId != null}
          >
            Ver detalle
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        selectedDetail ? 'Detalle de usuario bloqueado' : 'Usuarios bloqueados'
      }
      open={open}
      onCancel={handleClose}
      maskClosable={reviewingSignalId == null}
      closable={reviewingSignalId == null}
      footer={
        selectedDetail ? (
          <Space>
            <Button
              onClick={backToList}
              disabled={reviewingSignalId != null}
            >
              Volver
            </Button>
            <Button onClick={handleClose} disabled={reviewingSignalId != null}>
              Cerrar
            </Button>
          </Space>
        ) : (
          <Button onClick={handleClose}>Cerrar</Button>
        )
      }
      width={960}
      centered
      destroyOnClose
    >
      {selectedDetail ? (
        <HiddenUserDetailContent
          detail={selectedDetail}
          reviewingSignalId={reviewingSignalId}
          onPreviewVideo={onPreviewVideo}
          onReviewSignal={confirmReview}
        />
      ) : (
        <Spin spinning={loading}>
          <Table
            rowKey={(row) => row.hiddenClientId ?? row.client?.id}
            size="small"
            dataSource={items}
            columns={columns}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              showTotal: (t, range) =>
                `${range[0]}-${range[1]} de ${t} usuarios`,
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: 'No hay usuarios bloqueados',
            }}
          />
        </Spin>
      )}
    </Modal>
  );
};

export default HiddenUsersModal;
